import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button, InputField } from 'src/components';
import { setFromErrors } from 'src/helpers';
import { useGetFcpsCourseListsForLogbookEntryMutation, useLazyGetFormByLogbookTypeAndSubjectQuery } from 'src/rtk/features/setup/logBookEntrySetupDetails/logBookEntrySetupDetailsApi';
import { useCreateLogbookEntrySetupMasterMutation } from 'src/rtk/features/setup/logBookEntrySetupMaster/logBookEntrySetupMasterApi';
import { useGetSubjectInfosQuery } from 'src/rtk/features/setup/subjectInfo/subjectInfoApi';
import * as Yup from 'yup';

const initialValues: LogbookEntrySetupMaster = {
  id: '',
  setup_logbook_master_id: '',
  logbook_entry_setup_master_id: '',
  particulars: '',
  details: '',
  note: '',
  help: '',
  is_show_ind: 1,
  sl_no: 0,
  particulary_type: 1,
  course_type: '',
  target_minimum: 0,
  target_maximum: 0,
  status: true,
  creator_organization_id: '',
  updater_organization_id: '',
  created_by: '',
  updated_by: '',
  deleted_by: '',
  created_at: '',
  updated_at: '',
  deleted_at: '',
};

interface QueryParams {
  id?: number;
  course_type?: string;
  subject_id?: string;
  main_training?: string;
}

export const AddLogbookEntrySetupMaster = () => {
  const [createLogbookEntrySetupMaster, { isSuccess, isError, error }] = useCreateLogbookEntrySetupMasterMutation();
  const [getForms, { data: formSetups, isSuccess: isFormSetupsSuccess, isLoading: isFormSetupsLoading, isFetching: isFormSetupsFetching }] =
    useLazyGetFormByLogbookTypeAndSubjectQuery();

  const [showStatus, setShowStatus] = useState<boolean>(true);
  const [subject, setSubject] = useState<SubjectInfo>(
    {} as SubjectInfo
  );
  const [queryParams, setQueryParams] = useState<QueryParams>(
    {} as QueryParams,
  );
  const [selectedSubject, setSelectedSubject] = useState<any>('');

  const [mainTrainings, setMainTrainings] = useState<FcpsCourseList[]>([]);
  const [forms, setForms] = useState<LogbookEntrySetupMaster[]>([]);
  const [selectedForm, setSelectedForm] = useState<LogbookEntrySetupMaster | null>(null);
  const [selectedCaseTypeObject, setSelectedCaseTypeObject] = useState<LogbookEntrySetupMaster | null>(null);
  const [coreAdvanceOptions, setCoreAdvanceOptions] = useState<SelectOption[]>([]);

  // New state for entry type and case type
  const [entryType, setEntryType] = useState<string>(''); // 'case' or 'skill'
  const [caseTypes, setCaseTypes] = useState<any[]>([]);
  const [selectedCaseType, setSelectedCaseType] = useState<string>('');

  const [mainTraining, setMainTraining] = useState<FcpsCourseList>(
    {} as FcpsCourseList,
  );

  const { data: getSubjectInfolist } = useGetSubjectInfosQuery();
  const subjects = getSubjectInfolist?.data;

  let formOptions: SelectOption[] =
    forms?.map((e: LogbookEntrySetupMaster) => {
      return {
        label: e.particulars!,
        value: e.id!,
      };
    }) ?? [];


  // Case type options from selected form's entry_master_data
  const caseTypeOptions: SelectOption[] =
    caseTypes?.map((caseType: any) => ({
      label: caseType.particulars, // Adjust based on your data structure
      value: caseType.id,
    })) || [];

  const [createFcpsCourse, { data: fcpsCourseListData, isSuccess: isFcpsCourseListSuccess, isError: isFcpsCourseListError, error: fcpsCourseListError }] =
    useGetFcpsCourseListsForLogbookEntryMutation();

  const history = useHistory();

  const err: CustomError = error as CustomError;
  const { handleSubmit, handleChange, setFieldValue, getFieldProps, values, touched, errors, resetForm, setErrors } =
    useFormik({
      initialValues,
      validationSchema: Yup.object({
        setup_logbook_master_id: Yup.string().required('Please select the logbook master'),
      }),

      onSubmit: (values) => {
        if (showStatus) {
          values.is_show_ind = 1;
        } else {
          values.is_show_ind = 0;
        }

        createLogbookEntrySetupMaster({
          ...values,
        });
        console.log(values);
      },
    });

  useEffect(() => {
    if (isSuccess) {
      resetForm();
      toast.success('Logbook entry setup master created successfully');
      history.push('/setup/logbook-entry-setup-master');
    }
    if (isError) {
      toast.error(err?.message || 'Something went wrong');
      setFromErrors(err.data, setErrors);
    }
  }, [isSuccess, isError]);

  let subjectOptions: SelectOption[] =
    subjects?.map((subject) => ({
      value: subject.id!,
      label: subject.subject_name,
    })) || [];

  let mainTrainingOptions: SelectOption[] =
    mainTrainings?.map((mainTraining) => ({
      value: mainTraining.id!,
      label: mainTraining.name!,
    })) || [];

  useEffect(() => {
    if (subject?.id) {
      createFcpsCourse(subject?.id!);
    }
  }, [subject]);

  useEffect(() => {
    if (isFcpsCourseListSuccess && fcpsCourseListData?.success) {
      const mainTrainings: FcpsCourseList[] = fcpsCourseListData.data;
      setMainTrainings(mainTrainings);
    } else {
      setMainTrainings([]);
    }
  }, [isFcpsCourseListSuccess, fcpsCourseListData?.success]);

  const handleMainTrainingOption = (e: any) => {
    const { options, selectedIndex, value } = e.target;
    const mainTraining = mainTrainings?.find(
      (mainTraining) => mainTraining.id === value,
    );
    setMainTraining(mainTraining!);
    const newQueryParams: QueryParams = { ...queryParams };
    newQueryParams.main_training = mainTraining?.id;
    newQueryParams.course_type = '';
    setQueryParams(newQueryParams);
    setForms([]);

    if (value === '') {
      setCoreAdvanceOptions([]);
    } else {
      setCoreAdvanceOptions([
        { label: 'Core', value: 'core' },
        { label: 'Specialty', value: 'adv' },
      ]);
    }
  };

  const handleCoreAdvance = (e: any) => {
    const { options, selectedIndex, value } = e.target;
    values.course_type = value;
    const newQueryParams: QueryParams = { ...queryParams };
    newQueryParams.course_type = value;
    setQueryParams(newQueryParams);

    if (value === '') {
      setForms([]);
    } else {
      setForms(formSetups?.data!);
    }
  };

  const handleShowStatus = (e: any) => {
    setShowStatus(e.target.checked);
  };

  const handleEntryTypeChange = (e: any) => {
    const { value } = e.target;
    setEntryType(value);

    // Reset case type when entry type changes
    setSelectedCaseType('');
    setCaseTypes([]);

    // Reset particulars field
    setFieldValue('particulars', '');
  };

  useEffect(() => {
    const subject = subjects?.find((subject) => subject.id === selectedSubject?.value);
    setSubject(subject!);

    const newQueryParams: QueryParams = { ...queryParams };
    newQueryParams.subject_id = subject?.id;
    newQueryParams.main_training = '';
    newQueryParams.course_type = '';
    setQueryParams(newQueryParams);
    setCoreAdvanceOptions([]);
    setForms([]);

    setMainTraining({ id: '' } as FcpsCourseList);

    if (selectedSubject?.value === '') {
      setMainTrainings([]);
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (
      queryParams.subject_id &&
      queryParams.main_training &&
      queryParams.course_type
    ) {
      const updatedParams = { ...queryParams, id: 1 };
      getForms(updatedParams);
    }
  }, [queryParams]);

  useEffect(() => {
    if (formSetups && isFormSetupsSuccess) {
      setForms(formSetups?.data);
    }
  }, [formSetups, isFormSetupsSuccess]);

  const handleFormSelect = (e: any) => {
    const { options, selectedIndex, value } = e.target;
    const form = forms.find((form) => form.id === value);
    setSelectedForm(form || null);

    setSelectedCaseType('');

    console.log(form);
  };

  useEffect(() => {
    if (selectedForm && entryType === 'case') {
      setFieldValue('setup_logbook_master_id', selectedForm?.setup_logbook_master_id);
      setFieldValue('logbook_entry_setup_master_id', selectedForm?.id);
    }
    else if (selectedForm && entryType === 'skill') {
      if (selectedForm && selectedForm.entry_setup_master) {
        setCaseTypes(selectedForm.entry_setup_master);
      } else {
        setCaseTypes([]);
      }
    }

  }, [selectedForm, entryType])

  const handleCaseTypeSelect = (e: any) => {
    const { value } = e.target;
    const caseType = caseTypes.find((caseType) => caseType.id === value);
    setSelectedCaseTypeObject(caseType || null);

    setSelectedCaseType(value);
  };

  useEffect(() => {
    if (selectedCaseTypeObject) {
      setFieldValue('setup_logbook_master_id', selectedCaseTypeObject?.setup_logbook_master_id);
      setFieldValue('logbook_entry_setup_master_id', selectedCaseTypeObject?.id);
      console.log(selectedCaseTypeObject)
    }
  }, [selectedCaseTypeObject]);

  return (
    <>
      <div className="row align-items-center justify-content-center">
        <Form
          className="custom-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="row">
            <div className="col-lg-4 col-md-6 col-sm-12">
              <FormGroup className="mb-3">
                <Label className="form-label">Subject</Label>
                <Select
                  isClearable={true}
                  isSearchable={true}
                  value={selectedSubject}
                  onChange={setSelectedSubject}
                  options={subjectOptions}
                  placeholder="Select Subject"
                  name="subject"
                  id="subject"
                />
              </FormGroup>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12">
              <FormGroup className="mb-3">
                <Label className="form-label">Main Training</Label>
                <InputField
                  type="select"
                  name="mainTraining"
                  placeholder=""
                  value={mainTraining?.id}
                  options={mainTrainingOptions}
                  onChange={(e) => {
                    handleMainTrainingOption(e);
                  }}
                />
              </FormGroup>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12">
              <FormGroup className="mb-3">
                <Label className="form-label">Core/Speciality</Label>
                <InputField
                  type="select"
                  name="course_type"
                  placeholder=""
                  options={coreAdvanceOptions}
                  onChange={(e) => {
                    handleCoreAdvance(e);
                  }}
                />
              </FormGroup>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12">
              <FormGroup className="mb-3">
                <Label className="form-label required-field">Form</Label>
                <InputField
                  type="select"
                  name="setup_logbook_master_id"
                  placeholder=""
                  value={selectedForm?.id}
                  options={formOptions}
                  onChange={(e) => {
                    handleFormSelect(e);
                  }}
                  invalid={{ errors, touched }}
                />
              </FormGroup>
            </div>

            {/* New Entry Type Selection */}
            <div className='col-lg-8 col-md-8 col-sm-12 mt-4'>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="entry_type"
                  value="case"
                  onChange={(e) => {
                    setEntryType(e.target.value);
                  }}
                  checked={entryType === 'case'}
                  id="case-type"
                />
                <label
                  className="form-check-label"
                  htmlFor="case-type"
                >
                  Add Case Type
                </label>
              </div>

              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="entry_type"
                  value="skill"
                  onChange={(e) => {
                    setEntryType(e.target.value);
                  }}
                  checked={entryType === 'skill'}
                  id="skill-type"
                />
                <label
                  className="form-check-label"
                  htmlFor="skill-type"
                >
                  Add Skill Type
                </label>
              </div>
            </div>

            {/* Case Type Dropdown - Only show when skill type is selected */}
            {entryType === 'skill' && (
              <div className="col-lg-4 col-md-6 col-sm-12">
                <FormGroup className="mb-3">
                  <Label className="form-label">Case Type</Label>
                  <InputField
                    type="select"
                    name="case_type"
                    placeholder="Select case type"
                    value={selectedCaseType}
                    options={caseTypeOptions}
                    onChange={handleCaseTypeSelect}
                  />
                </FormGroup>
              </div>
            )}

            {/* Particulars Field - Show when case type is selected OR when skill type is selected */}
            {(entryType === 'case' || entryType === 'skill') && (
              <div className="col-lg-4 col-md-6 col-sm-12">
                <FormGroup className="mb-3">
                  <Label for="particulars">Particulars</Label>
                  <InputField
                    type="textarea"
                    placeholder="Write here..."
                    invalid={{ errors, touched }}
                    {...getFieldProps('particulars')}
                  />
                </FormGroup>
              </div>
            )}

            {/* <div className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for="details">Details</Label>
                      <InputField
                        type="textarea"
                        placeholder="Details"
                        invalid={{ errors, touched }}
                        {...getFieldProps('details')}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for="note">Note</Label>
                      <InputField
                        type="text"
                        placeholder="Note"
                        invalid={{ errors, touched }}
                        {...getFieldProps('note')}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for="help">Help</Label>
                      <InputField
                        type="text"
                        placeholder="Help"
                        invalid={{ errors, touched }}
                        {...getFieldProps('help')}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for="sl_no">Sl No</Label>
                      <InputField
                        type="number"
                        placeholder="Sl No"
                        invalid={{ errors, touched }}
                        {...getFieldProps('sl_no')}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for="particulary_type">Particulary Type</Label>
                      <InputField
                        type="number"
                        placeholder="Particulary Type"
                        invalid={{ errors, touched }}
                        {...getFieldProps('particulary_type')}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for="target_minimum">Target Minimum</Label>
                      <InputField
                        type="number"
                        invalid={{ errors, touched }}
                        {...getFieldProps('target_minimum')}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for="target_maximum">Target Maximum</Label>
                      <InputField
                        type="number"
                        invalid={{ errors, touched }}
                        {...getFieldProps('target_maximum')}
                      />
                    </FormGroup>
                  </div> */}

            <div className="col-sm-12">
              <FormGroup check className="mb-3 mt-4">
                <Label check>
                  <Input
                    name='is_show_ind'
                    id='is_show_ind'
                    type="checkbox"
                    onChange={(e) => {
                      handleShowStatus(e);
                      handleChange(e);
                    }}
                    checked={showStatus}
                  />{' '}
                  Show
                </Label>
              </FormGroup>
            </div>

            <div className="col-sm-12">
              <FormGroup check className="mb-3 mt-4">
                <Label check>
                  <Input type="checkbox" checked={values.status} {...getFieldProps('status')} />{' '}
                  Status
                </Label>
              </FormGroup>
            </div>
          </div>
          <Button type="submit" text="Submit" />
        </Form>
      </div>
    </>
  );
};